import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, skip } from 'rxjs/operators';
import { Borrower } from '../../../models/borrower-model';
import { TcpaConsentStep } from '../../../models/wizard/tcpa-consent-step-model';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { FeatureFlags } from '../../../models/feature-flags.model';
import { localizeField } from '../../../transloco/localize-field';

export type TcpaConsentSelection = 'Consent' | 'NonConsent';

@Component({
    selector: 'tcpa-consent-step',
    templateUrl: 'tcpa-consent-step.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TcpaConsentStepComponent extends WizardStepComponentBase<TcpaConsentStep> implements OnInit, OnDestroy {

  showTcpaConsentVerbiage: boolean = false;
  tcpaConsentText: string | undefined = '';

  mainBorrower!: Borrower;

  tcpaConsentSelection: TcpaConsentSelection | undefined;

  private _tcpaConsentGenerationEnabled: boolean = false;
  private _config: FeatureFlags | undefined;
  private _langSub?: Subscription;

  get canMoveToNextStep(): boolean {
    return this.tcpaConsentSelection === 'Consent' || this.tcpaConsentSelection === 'NonConsent';
  }

  constructor(private readonly _injector: Injector,
    private readonly _cdr: ChangeDetectorRef) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    if (this.currentBorrower) {
      this.mainBorrower = this.currentBorrower;
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.startSpinner();
    this.mortgageApplicationService.getConfig(this.mortgageApplication.applicationId).pipe(
      finalize(() => {
        this.stopSpinner();
        this._cdr.markForCheck();
      })
    )
      .subscribe(response => {
        this._config = response;
        this.refreshTcpaConsentText();
        this.showTcpaConsentVerbiage = response.showTcpaConsentVerbiage || false;
        this._tcpaConsentGenerationEnabled = response.tcpaConsentGenerationEnabled || false;
        this._cdr.markForCheck();
      });
    this._langSub = this.translocoService.langChanges$.pipe(skip(1)).subscribe(() => {
      this.refreshTcpaConsentText();
      this._cdr.markForCheck();
    });
  }

  private refreshTcpaConsentText(): void {
    this.tcpaConsentText = localizeField(this._config, 'tcpaConsentText', this.translocoService.getActiveLang());
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._langSub?.unsubscribe();
  }

  onNextClicked() {
    if (!this.tcpaConsentSelection || !this.mainBorrower?.contactId) {
      super.onNextClicked();
      return;
    }

    const submit$ = this.mortgageApplicationService.submitTcpaConsent(
      this.mortgageApplication.applicationId,
      this.mainBorrower.contactId,
      this.tcpaConsentSelection === 'Consent'
    );

    const apiCalls: any[] = [submit$];

    if (this._tcpaConsentGenerationEnabled && this.tcpaConsentSelection === 'Consent') {
      apiCalls.push(this.mortgageApplicationService.generateTcpaConsentDocument(this.mortgageApplication.applicationId,
        { tcpaConsentText: this.tcpaConsentText, borrowerId: this.mainBorrower.contactId })
        .pipe(catchError(() => of(null))));
    }

    this.startSpinner();
    forkJoin(apiCalls).subscribe({
      next: () => {
        this.stopSpinner();
        super.onNextClicked();
      },
      error: () => {
        this.stopSpinner();
        super.onNextClicked();
      }
    });
  }
}
